import * as d3 from 'd3';
import moment from 'moment';
import React from 'react';
import isEqual from 'react-fast-compare';
import _ from 'underscore';

import {
  CalendarInteraction,
  CalendarItem as CalendarItemType,
  ProjectColorMapping,
} from 'calendar/CalendarConstants';
import DataConnector from 'connect/DataConnector';
import {AvatarSize} from 'ui-library/avatar/AvatarConstants';
import ModalAnchor from 'ui-library/ModalAnchor';
import PartyAvatarList from 'ui-library/PartyAvatarList';
import {ElementMouseEvent} from 'util/Types';

import CalendarActions from './CalendarActions';
import CalendarItemModal from './CalendarItemModal';
import CalendarStore from './CalendarStore';
import CalendarUtil, {isSelectedItemInteraction} from './CalendarUtil';

interface CalendarItemDayProps {
  date: moment.Moment;
  item: CalendarItemType;
  widthPercentage: number;
  selected: boolean;
}

interface EnhancedProps {
  projectColorMapping: ProjectColorMapping;
}

class CalendarItemDayConnector extends DataConnector<CalendarItemDayProps, EnhancedProps> {
  shouldSubscribeToRedux = false;
  stores = [CalendarStore];

  enhanceProps(): EnhancedProps {
    return {
      projectColorMapping: this.calendarStore().getProjectColorMapping(),
    };
  }
}

interface CalendarItemDayState {
  isHovered: boolean;
}

class CalendarItemDay extends React.Component<CalendarItemDayProps & EnhancedProps, CalendarItemDayState> {
  state: CalendarItemDayState = {
    isHovered: false,
  };

  _handleCloseModal = (): void => {
    CalendarActions.deselectCalendarItem();
  };

  _handleClick = (): void => {
    CalendarActions.selectCalendarItem(this.props.item.id, this.props.date);
  };

  _handleMouseOver = (): void => {
    this.setState({isHovered: true});
  };

  _handleMouseOut = (): void => {
    this.setState({isHovered: false});
  };

  backgroundColor = (): string => {
    const project = CalendarUtil.getCalenderItemProject(this.props.item);
    return d3.rgb(this.props.projectColorMapping[project.id]).brighter(0.5).formatHex();
  };

  highlightedBackgroundColor = (): string => {
    const baseColor = this.backgroundColor();
    return d3.rgb(baseColor).brighter(0.1).formatHex();
  };

  render(): JSX.Element {
    return (
      <div style={{width: `${this.props.widthPercentage}%`}} className="calendarItemDay-container">
        <div
          className="u-pointer calendarItemDay-content"
          style={{
            backgroundColor: this.state.isHovered
              ? this.highlightedBackgroundColor()
              : this.backgroundColor(),
          }}
          onClick={this._handleClick}
          onMouseOver={this._handleMouseOver}
          onMouseOut={this._handleMouseOut}
        >
          <ModalAnchor open={this.props.selected}>
            <CalendarItemModal
              onRequestClose={this._handleCloseModal}
              item={CalendarUtil.getCalenderItemProject(this.props.item)}
              date={this.props.date}
            />
          </ModalAnchor>
        </div>
      </div>
    );
  }
}

const ConnectedCalendarItemDay = CalendarItemDayConnector.connect(CalendarItemDay);

export interface CalendarItemProps {
  date: moment.Moment;
  item?: CalendarItemType;
  numDays?: number;
  onMouseDown?: (e: ElementMouseEvent) => void;
  onMouseOver?: () => void;
  interact?: CalendarInteraction;
}

class CalendarItem extends React.Component<CalendarItemProps> {
  static defaultProps = {
    numDays: 1,
  };

  shouldComponentUpdate(nextProps: CalendarItemProps): boolean {
    return !isEqual(this.props, nextProps);
  }

  renderCalendarItem(item: CalendarItemType): JSX.Element {
    return (
      <div className="calendarItem-position">
        <div className="calendarItem-dayContainer">
          {this.props.numDays != null &&
            _.range(this.props.numDays).map((numDay) => {
              const date = this.props.date.clone().add(numDay, 'days');
              const selected =
                this.props.interact != null &&
                isSelectedItemInteraction(this.props.interact) &&
                this.props.interact.selectedCalendarItemDay.id === item.id &&
                date.isSame(this.props.interact.selectedCalendarItemDay.date);

              return (
                <ConnectedCalendarItemDay
                  key={numDay}
                  date={date}
                  item={item}
                  widthPercentage={100 / this.props.numDays!}
                  selected={selected}
                />
              );
            })}
        </div>
        <div className="calendarItem-content">
          <PartyAvatarList
            avatarClassName="u-valign-top"
            className="u-display-inline"
            maxAvatars={3}
            parties={CalendarUtil.getItemParties(item)}
            size={AvatarSize.XXSMALL}
          />
          <span className="calendarItem-name">{item.name}</span>
        </div>
      </div>
    );
  }

  render(): JSX.Element {
    return (
      <td
        colSpan={this.props.numDays}
        className="calendarItem"
        onMouseDown={this.props.onMouseDown}
        onMouseOver={this.props.onMouseOver}
      >
        {this.props.item != null ? (
          this.renderCalendarItem(this.props.item)
        ) : (
          // Render empty position div for spacing.
          <div className="calendarItem-position" />
        )}
      </td>
    );
  }
}

export default CalendarItem;
